import { useEffect, useState, useCallback } from 'react'
import { IcaTable, HeaderItemClickDetail } from '../IcaElements/IcaTable'
import { IcaCheckbox } from '../IcaElements/IcaCheckbox'
import '@ica-azure/ica-elements/IcaIconError'
import '@ica-azure/ica-elements/IcaIconTrash'
import StoreGroceryStock from '../../types/StoreGroceryStock'
import GroceryFeedTableRow from './GroceryFeedTableRow'
import { SortDirection } from '../../utils/sortUtils'
import { compareRunningOutThreshold } from '../../utils/stockUtils'

interface HeaderItem {
  label: string
  column: number
  sortdirection?: 'default' | SortDirection
  id: string
  tooltipText?: string
}

interface TableSort {
  columnNumber: number | undefined
  direction: SortDirection
}

interface GroceryFeedTableProps {
  data: StoreGroceryStock[]
  selectedArticles: string[]
  allArticles: string[]
  onSelectArticle: (ean: string) => void
  onSelectAll: () => void
  onDeselectAll: () => void
  onCustomPriceChange: (item: StoreGroceryStock, newValue: number | null | undefined) => Promise<void>
  onCustomPriceDelete: (item: StoreGroceryStock) => Promise<void>
  onDeleteArticle: (item: StoreGroceryStock) => void
  onChangeStockSettings?: (item: StoreGroceryStock, threshold?: number, maxQuantity?: number) => Promise<void>
}

export default function GroceryFeedTable({
  data,
  selectedArticles,
  allArticles,
  onSelectArticle,
  onSelectAll,
  onDeselectAll,
  onCustomPriceChange,
  onCustomPriceDelete,
  onDeleteArticle,
  onChangeStockSettings,
}: GroceryFeedTableProps) {
  const isAllSelected = selectedArticles.length > 0 || selectedArticles.length === allArticles.length && selectedArticles.length > 0
  const isPartiallySelected = selectedArticles.length > 0 && selectedArticles.length < allArticles.length
  const [sortedData, setSortedData] = useState<StoreGroceryStock[]>([...data])
  const [currentSorting, setCurrentSorting] = useState<TableSort>({ columnNumber: undefined, direction: 'desc', })
  const [headerItems, setHeaderItems] = useState<HeaderItem[]>([
    {
      label: 'Artikel',
      column: 1,
      sortdirection: 'default',
      id: 'column1',
    },
    {
      label: 'Ord. pris kr',
      column: 2,
      sortdirection: 'default',
      id: 'column2',
    },
    {
      label: 'Lokalt pris kr',
      column: 3,
      sortdirection: 'default',
      id: 'column3',
    },
    {
      label: 'Senast påfylld',
      column: 4,
      sortdirection: 'default',
      id: 'column4',
    },
    {
      label: 'Antal sålda',
      column: 5,
      sortdirection: 'default',
      id: 'column5',
    },
    {
      label: 'I skåp / max',
      column: 6,
      sortdirection: 'default',
      id: 'column6',
      tooltipText: 'Antal i skåp just nu / maxsaldo (valfritt maxsaldo per artikel)'
    },
    {
      label: 'Gränsvärde',
      column: 7,
      sortdirection: 'default',
      id: 'column7',
      tooltipText: '"Snart slut" triggas automatiskt när antalet i skåp blir lågt: 30 % av basvärdet vid 2–5, 20 % vid 6 eller fler. Vid 1 visas det direkt. Basen är maxsaldot om det är satt, annars senast påfyllt antal. Gränsen kan ändras per artikel.'
    },
    {
      label: 'Status',
      column: 8,
      id: 'column8',
    },
  ])

  const getSortValue = useCallback((item: StoreGroceryStock, columnNumber: number): string | number => {
    switch (columnNumber) {
      case 1: // Artikel
        return item.name
      case 2: // Ord. pris
        return item.ordinaryPrice || 0
      case 3: // Lokalt pris
        return item.customPrice || 0
      case 4: // Senast påfylld
        return item.restockedAt
      case 5: // Antal sålda
        return item.restockedQuantity - item.quantity
      case 6: // Antal i skåp
        return item.quantity
      case 7: // Gränsvärde
        return item.runningOutThreshold || 0
      default:
        return ''
    }
  }, [])

  const sort = useCallback(
    (dataToSort: StoreGroceryStock[], sorting: TableSort): StoreGroceryStock[] => {
      if (!dataToSort || dataToSort.length === 0) {
        return []
      } else if (sorting.columnNumber === undefined) {
        return dataToSort
      }

      const sortModifier: number = sorting.direction === 'asc' ? 1 : -1

      return [...dataToSort].sort((a, b) => {
        if (sorting.columnNumber === 7) {
          return compareRunningOutThreshold(a, b, sorting.direction)
        }

        const aVal = getSortValue(a, sorting.columnNumber!)
        const bVal = getSortValue(b, sorting.columnNumber!)

        switch (typeof aVal) {
          case 'string':
            return (aVal as string).localeCompare(bVal as string, 'sv') * sortModifier
          case 'number':
            return ((aVal as number) - (bVal as number)) * sortModifier
          default:
            return 0
        }
      })
    },
    [getSortValue]
  )

  useEffect(() => {
    const newData = sort(data, currentSorting)
    setSortedData([...newData])
  }, [data, currentSorting, sort])

  const handleHeaderClick = (e: CustomEvent<HeaderItemClickDetail>) => {
    const clickedItem = e.detail
    const clickedColumnIndex = clickedItem.column

    // Return for columns that are not sortable
    if (clickedColumnIndex === 8) {
      return
    }

    const getNextSortDirection = (current: string): SortDirection => {
      if (current === 'default' || current === 'desc') return 'asc'
      return 'desc'
    }

    const newDirection = getNextSortDirection(clickedItem.sortdirection || 'default')

    setHeaderItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === clickedItem.id) {
          return {
            ...item,
            sortdirection: newDirection,
          }
        }
        // Reset other columns to default when a new column is clicked (only if they have sortdirection)
        if (item.sortdirection !== undefined) {
          return {
            ...item,
            sortdirection: 'default',
          }
        }
        return item
      })
    )

    // Update sorting
    setCurrentSorting({
      columnNumber: clickedColumnIndex,
      direction: newDirection,
    })
  }

  const handleSelectAllToggle = () => {
    if (selectedArticles.length === allArticles.length || selectedArticles.length > 0 && selectedArticles.length < allArticles.length) {
      onDeselectAll()
    } else {
      onSelectAll()
    }
  }

  return (
    <IcaTable
      id="grocery-feed-table"
      headeritems={JSON.stringify(headerItems)}
      style={{ '--IcaTable--header-columns': '2.5fr repeat(4, 1.5fr) 1.5fr 2fr 1fr 1rem' }}
      onHeaderitemclick={handleHeaderClick}
    >
      <IcaCheckbox
        slot="left"
        id="table-header-checkbox"
        checked={isAllSelected}
        multiple={isPartiallySelected}
        onChange={handleSelectAllToggle}
      />

      {sortedData.map((item, index) => (
        <GroceryFeedTableRow
          key={item.ean}
          item={item}
          even={index % 2 === 0}
          selectedArticles={selectedArticles}
          onSelectArticle={onSelectArticle}
          onCustomPriceChange={onCustomPriceChange}
          onCustomPriceDelete={onCustomPriceDelete}
          onDeleteArticle={onDeleteArticle}
          onChangeStockSettings={onChangeStockSettings}
        />
      ))}
    </IcaTable>
  )
}
